import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

/**
 * Reusable Master CRUD page used by Marketplace masters (Categories, Brands…)
 * and Product masters (Sizes, Finishes, Materials…). Props:
 *   kind:        URL path segment used by /api/admin/masters/{kind}
 *   title:       Page heading
 *   description: short helper text
 *   extra:       optional list of extra meta fields {key, label, type}
 */
export default function MasterCrudPage({ kind, title, description, extra = [] }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", description: "", active: true, meta: {} });

  const load = async () => {
    const { data } = await api.get(`/admin/masters/${kind}`);
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const openCreate = () => { setEditing(null); setForm({ name: "", code: "", description: "", active: true, meta: {} }); setOpen(true); };
  const openEdit = (i) => { setEditing(i); setForm({ name: i.name, code: i.code || "", description: i.description || "", active: i.active !== false, meta: i.meta || {} }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/admin/masters/${kind}/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post(`/admin/masters/${kind}`, form);
        toast.success("Created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (i) => {
    if (!window.confirm(`Delete "${i.name}"?`)) return;
    try { await api.delete(`/admin/masters/${kind}/${i.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8 space-y-6" data-testid={`master-${kind}-page`}>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl font-black">{title}</h1>
          {description && <p className="text-muted-foreground">{description}</p>}
        </div>
        <Button onClick={openCreate} data-testid={`master-${kind}-create`}><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Description</TableHead>
              {extra.map((e) => <TableHead key={e.key}>{e.label}</TableHead>)}
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`master-${kind}-row-${i.id}`}>
                <TableCell className="font-semibold">{i.name}</TableCell>
                <TableCell className="font-mono text-xs">{i.code || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{i.description || "—"}</TableCell>
                {extra.map((e) => <TableCell key={e.key} className="text-sm">{(i.meta || {})[e.key] ?? "—"}</TableCell>)}
                <TableCell>{i.active !== false ? <Badge>On</Badge> : <Badge variant="outline">Off</Badge>}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(i)} data-testid={`master-${kind}-edit-${i.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(i)} data-testid={`master-${kind}-delete-${i.id}`}><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5 + extra.length} className="text-center text-muted-foreground py-8">No entries.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit entry" : "Create entry"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`master-${kind}-name`} /></div>
            <div><Label>Code (optional)</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid={`master-${kind}-code`} /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid={`master-${kind}-desc`} /></div>
            {extra.map((e) => (
              <div key={e.key}>
                <Label>{e.label}</Label>
                <Input type={e.type || "text"} value={form.meta[e.key] || ""} onChange={(ev) => setForm({ ...form, meta: { ...form.meta, [e.key]: ev.target.value } })} data-testid={`master-${kind}-${e.key}`} />
              </div>
            ))}
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid={`master-${kind}-active`} /> <Label>Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid={`master-${kind}-save`}>{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
